import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ServiceCategoryManager } from "@/components/sales/ServiceCategoryManager";

interface InvoiceFormProps {
  onSuccess: () => void;
}

export const InvoiceForm = ({ onSuccess }: InvoiceFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: "",
    amount: "",
    due_date: "",
    service_category: "",
  });

  useEffect(() => {
    fetchClients();
    fetchCategories();
  }, []);

  const fetchClients = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("clients")
      .select("*")
      .eq("user_id", user.id)
      .order("name");

    if (data) setClients(data);
  };

  const fetchCategories = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('service_categories')
      .select('*')
      .eq('user_id', user.id)
      .order('name');

    if (data) setCategories(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const amount = parseFloat(formData.amount);
      const invoiceNumber = `INV-${Date.now()}`;

      const { error } = await supabase.from("invoices").insert({
        user_id: user.id,
        client_id: formData.client_id || null,
        invoice_number: invoiceNumber,
        subtotal: amount,
        total: amount,
        due_date: formData.due_date || null,
        status: "draft",
        invoice_type: "one_time",
        service_category: formData.service_category || null,
      });

      if (error) throw error;

      toast({
        title: "Invoice Created",
        description: "Your invoice has been created successfully.",
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create invoice. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {clients.length > 0 ? (
        <div>
          <Label htmlFor="client">Client</Label>
          <Select
            value={formData.client_id}
            onValueChange={(value) => setFormData({ ...formData, client_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No clients found. Create a client first to link invoices.
        </p>
      )}

      <div>
        <Label htmlFor="amount">Amount (₹) *</Label>
        <Input
          id="amount"
          type="number"
          step="0.01"
          required
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="serviceCategory">Revenue Source</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsCategoryManagerOpen(true)}
            className="h-8 text-xs"
          >
            <Settings className="h-3 w-3 mr-1" />
            Manage
          </Button>
        </div>
        <Select 
          value={formData.service_category} 
          onValueChange={(value) => setFormData({ ...formData, service_category: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select revenue source (optional)" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.name}>
                {cat.name}
              </SelectItem>
            ))}
            {categories.length === 0 && (
              <div className="p-2 text-sm text-muted-foreground text-center">
                No revenue sources found. Click "Manage" to add.
              </div>
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">Which service is this invoice for?</p>
      </div>

      <div>
        <Label htmlFor="due_date">Due Date</Label>
        <Input
          id="due_date"
          type="date"
          value={formData.due_date}
          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
        />
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Creating..." : "Create Invoice"}
      </Button>

      <ServiceCategoryManager
        isOpen={isCategoryManagerOpen}
        onClose={() => {
          setIsCategoryManagerOpen(false);
          fetchCategories();
        }}
      />
    </form>
  );
};
